import { api, idr, fmtNum } from "../lib/api";
import { User, Wallet, CalendarDays, IdCard } from "lucide-react";
import { motion } from "framer-motion";
import DashboardProfile from "../components/hris/DashboardProfile";
import ThemeControl from "@/components/ui/ThemeControl";
import { API } from "../lib/api";
import LoadingState from "@/components/ui/loading-state";
import { useDataQuery } from "@/hooks/useDataQuery";

// Diekspor supaya Layout.jsx bisa prefetch query yang sama persis saat kursor
// hover menu "Beranda" (portal karyawan) cukup lama.
export const MY_PROFILE_QK = ["my-profile"];
export const MY_CUTI_QK = ["my-cuti"];
export const MY_SLIPS_QK = ["my-slips"];

export default function MyDashboard() {
  // Tanpa penanganan gagal, satu error (akun belum ditautkan ke data karyawan, koneksi
  // putus, server 500) membuat profil selamanya kosong dan halaman berhenti di "Memuat..."
  // tanpa pesan apa pun — user tidak tahu harus berbuat apa dan mengira aplikasi hang.
  const { data, error: gagalErr, refetch: refetchProfil } = useDataQuery(
    MY_PROFILE_QK, () => api.get("/my/profile").then(r => r.data),
  );
  const { data: cuti, refetch: refetchCuti } = useDataQuery(
    MY_CUTI_QK, () => api.get("/my/cuti").then(r => r.data),
  );
  const { data: slipsData, error: slipsErr, refetch: refetchSlips } = useDataQuery(
    MY_SLIPS_QK, () => api.get("/my/slips").then(r => r.data),
  );
  const slips = slipsData || [];
  const slipsGagal = !!slipsErr;
  const cobaLagi = () => { refetchProfil(); refetchCuti(); refetchSlips(); };

  if (gagalErr) return (
    <div className="p-8" data-testid="my-dashboard-error">
      <div className="max-w-md bg-rose-50 border border-rose-200 rounded-xl p-5">
        <p className="font-display font-semibold text-rose-900">Beranda tidak bisa dimuat</p>
        <p className="text-sm text-rose-800 mt-1">
          {gagalErr?.response?.data?.detail || "Gagal memuat data karyawan Anda. Periksa koneksi lalu coba lagi."}
        </p>
        <button onClick={cobaLagi} data-testid="my-dashboard-retry"
          className="mt-4 h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium">
          Coba Lagi
        </button>
      </div>
    </div>
  );
  if (!data) return <div className="p-4 md:p-8"><LoadingState loading data-testid="my-dashboard-loading" /></div>;
  const { employee: e, grade: g } = data;
  const totalTetap = (g?.gaji_pokok || 0) + (g?.tunj_jabatan || 0) + (g?.tunj_makan || 0);

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <DashboardProfile employee={e} />
        <div className="flex gap-2 flex-wrap"><ThemeControl compact />
        <button onClick={async () => {
          const token = localStorage.getItem("hris_token");
          const res = await fetch(`${API}/my/id-card`, { headers: { Authorization: `Bearer ${token}` } });
          const blob = await res.blob(); const url = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = url; a.download = `IDCard_${e.nama}.pdf`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        }} data-testid="btn-my-idcard"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F172A] hover:bg-slate-800 text-white text-sm font-medium transition-colors">
          <IdCard className="w-4 h-4" /> Cetak Kartu Karyawan
        </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-6 md:mb-8">
        <Kpi icon={User} label="NIK" value={e.nik} tint="slate" />
        
        <Kpi icon={Wallet} label="Total Pendapatan Tetap" value={idr(totalTetap)} tint="amber" />
        <Kpi icon={CalendarDays} label="Sisa Cuti" value={`${cuti?.saldo.sisa ?? "-"} / ${cuti?.saldo.jatah ?? "-"} hari`} tint="sky" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Profil Anda</h3>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row label="Nama" value={e.nama} />
            <Row label="Email" value={e.email} />
            <Row label="Branch" value={e.branch} />
            <Row label="Status" value={e.status_karyawan} />
            <Row label="Jenis Kelamin" value={e.jk === "L" ? "Laki-laki" : "Perempuan"} />
            <Row label="Agama" value={e.agama} />
            <Row label="Status Pernikahan" value={e.status_pernikahan} />
            <Row label="Tanggal Masuk" value={e.tanggal_masuk} />
            {e.status_karyawan === "PKWT" && e.tgl_habis_kontrak && <Row label="Habis Kontrak" value={e.tgl_habis_kontrak} />}
            <Row label="No. HP" value={e.no_hp || "-"} />
          </dl>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Slip Gaji Terakhir</h3>
          {/* Bedakan "memang belum ada" dari "gagal dimuat" — keduanya kalau disamakan
              membuat karyawan mengira slip gajinya belum terbit padahal cuma gagal diambil. */}
          {slipsGagal ? (
            <p className="text-sm text-rose-700">Gagal memuat slip gaji. Coba muat ulang halaman.</p>
          ) : slips.length === 0 ? (
            <p className="text-sm text-slate-500">Belum ada slip gaji</p>
          ) : (
            <div className="space-y-3">
              {slips.slice(0, 3).map(s => (
                <div key={s.id} className="border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Periode</p>
                  <p className="font-semibold text-slate-900">{s.periode}</p>
                  <p className="mt-2 text-xs text-slate-500">Take Home Pay</p>
                  <p className="font-display font-bold text-lg text-emerald-700">{idr(s.calc.thp)}</p>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint = "emerald" }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-4`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-sm md:text-base font-bold text-slate-900 truncate">{value}</p>
    </motion.div>
  );
}
function Row({ label, value }) {
  return (
    <>
      <dt className="text-xs text-slate-500 self-center">{label}</dt>
      <dd className="text-sm font-medium text-slate-900">{value || "-"}</dd>
    </>
  );
}
